import React, { useMemo } from 'react';
import { Box, Typography, CircularProgress, Alert, Stack, useTheme } from '@mui/material';
import { LineChart as MuiLineChart, lineElementClasses } from '@mui/x-charts/LineChart';
import { LineChart } from '../../../../../../shared/components/charts';
import { useReportWeather, useReportWeatherAnomalies, useReportTrends } from '../../hooks/useReportData';
import { METRIC_OPTIONS } from '../../utils/metricOptions';
import { mergeWeatherMetricSeries } from '../../utils/mergeWeatherMetricSeries';
import type { WeatherImpactBlock } from '../../api/types';
import type { WeatherTrendPoint, WeatherAnomaly, WeatherAnomalySeverity } from '../../../../../../shared/api/weather';

interface WeatherImpactViewProps {
  block: WeatherImpactBlock;
  clientId: string | null;
  branding?: { primaryColor?: string; chartColor?: string };
  fill?: boolean;
}

// Stable empty reference so the overlay memo below stays reference-stable when
// the metric query has no data (a fresh `[]` each render would defeat the memo).
const EMPTY_ROWS: Array<Record<string, unknown>> = [];

const severityToAlert = (severity: WeatherAnomalySeverity): 'info' | 'warning' | 'error' => {
  switch (severity) {
    case 'severe':
      return 'error';
    case 'warning':
      return 'warning';
    default:
      return 'info';
  }
};

/**
 * Weather vs wellbeing overlay. Plots the weather trend (temperature /
 * precipitation) over the period and lists anomaly callouts as alerts. Robust
 * to missing trend or anomaly data.
 */
export const WeatherImpactView: React.FC<WeatherImpactViewProps> = ({ block, clientId, branding, fill = false }) => {
  const theme = useTheme();
  const tempColor = branding?.chartColor ?? branding?.primaryColor ?? theme.palette.primary.main;
  const trendsQuery = useReportWeather(block.filters, clientId, block.ukRegion);
  const anomaliesQuery = useReportWeatherAnomalies(block.filters, clientId, block.ukRegion);

  const trends: WeatherTrendPoint[] = trendsQuery.data?.data?.trends ?? [];
  const anomalies: WeatherAnomaly[] = anomaliesQuery.data?.data?.anomalies ?? [];

  // Optional wellbeing-metric overlay: when the block names a metric, its trend
  // is fetched and drawn on a second (left) axis over the weather series. The
  // weather endpoint always returns one row per calendar day, so the metric is
  // fetched at daily granularity too - that keeps the two series aligned day for
  // day (a coarser bucket would only match on period-start days and drop out).
  // `metric` is typed as required, but read defensively for older saved blocks.
  const metricId = ((block.metric as string | undefined) ?? '').trim();
  const metricLabel = METRIC_OPTIONS.find((m) => m.id === metricId)?.label ?? 'Metric';
  const metricTrendQuery = useReportTrends(metricId, 'daily', block.filters, clientId);
  const metricTrend = (metricTrendQuery.data?.data?.trend ?? EMPTY_ROWS) as Array<Record<string, unknown>>;

  const chartData = useMemo(
    () =>
      trends.map((t) => ({
        label: t.label,
        temperature: t.temperature,
        precipitation: t.precipitation,
      })),
    [trends],
  );

  const overlayData = useMemo(
    () => (metricId ? mergeWeatherMetricSeries(chartData, metricTrend, metricId) : []),
    [metricId, chartData, metricTrend],
  );
  const hasMetric = metricId !== '' && overlayData.some((p) => p.metric !== null);

  if (trendsQuery.isLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
        <CircularProgress size={28} />
      </Box>
    );
  }

  if (trendsQuery.isError) {
    return <Alert severity='error'>Couldn't load weather data.</Alert>;
  }

  if (chartData.length === 0) {
    return <Alert severity='info'>No weather data for this period.</Alert>;
  }

  return (
    <Box sx={{ width: '100%' }}>
      {block.title && (
        <Typography variant='h6' sx={{ fontWeight: 700, mb: 2 }}>
          {block.title}
        </Typography>
      )}

      {hasMetric ? (
        <MuiLineChart
          dataset={overlayData}
          height={fill ? 420 : 320}
          xAxis={[{ scaleType: 'point', dataKey: 'label', disableTicks: true }]}
          yAxis={[
            { id: 'metric', label: metricLabel, position: 'left' },
            { id: 'weather', label: 'Weather', position: 'right' },
          ]}
          series={[
            {
              dataKey: 'metric',
              label: metricLabel,
              yAxisId: 'metric',
              color: theme.palette.secondary.main,
              connectNulls: true,
              showMark: false,
            },
            {
              dataKey: 'temperature',
              label: 'Temperature (C)',
              yAxisId: 'weather',
              color: tempColor,
              area: true,
              connectNulls: true,
              showMark: false,
            },
            {
              dataKey: 'precipitation',
              label: 'Precipitation (mm)',
              yAxisId: 'weather',
              color: theme.palette.info.main,
              connectNulls: true,
              showMark: false,
            },
          ]}
          margin={{ left: 8, right: 8, top: 20, bottom: 24 }}
          grid={{ vertical: true, horizontal: true }}
          sx={{
            width: '100%',
            [`& .${lineElementClasses.root}[data-series='temperature']`]: {
              strokeOpacity: 0.9,
            },
          }}
        />
      ) : (
        <LineChart
          data={chartData}
          xAxisKey='label'
          series={[
            {
              dataKey: 'temperature',
              label: 'Temperature (C)',
              color: tempColor,
              area: true,
            },
            {
              dataKey: 'precipitation',
              label: 'Precipitation (mm)',
              color: theme.palette.info.main,
            },
          ]}
          height={fill ? 420 : 320}
        />
      )}

      {anomalies.length > 0 && (
        <Stack spacing={1} sx={{ mt: 2 }}>
          <Typography variant='subtitle2' sx={{ fontWeight: 700 }}>
            Weather anomalies
          </Typography>
          {anomalies.map((a, i) => (
            <Alert key={`${a.kind}-${i}`} severity={severityToAlert(a.severity)}>
              <strong>{a.label}</strong> - {a.startDay}
              {a.endDay && a.endDay !== a.startDay ? ` to ${a.endDay}` : ''}
              {a.isForecast ? ' (forecast)' : ''}
            </Alert>
          ))}
        </Stack>
      )}
    </Box>
  );
};
